import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import {
  CloseOutlined,
  EditOutlined,
  ImportOutlined,
  LinkOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import { Button, Input, Popconfirm, Popover, Tooltip } from 'antd';

import ActionButton from 'src/components/general-ui/Buttons/ActionButton';
import { UUID } from 'src/types/utility';
import {
  FacilityMappingStrategyState,
  OperatorTenantType,
} from 'src/routes/Customers/components/OperatorsTab/modules/types';
import { DATA_STRATEGY } from 'src/utils/constants';
import { ActionMessage, ActionsButtonsWrap } from './styled';
import SelectBoxForm from 'src/components/general-ui/SelectBoxForm';
import { extractSelectOptions } from 'src/components/CrudCommonComponents/utils';
import { intl } from 'src/utils/errorHandling/messages';
import { PropsFromRedux } from '../Connector';
import { FacilityLabel } from '../style';

type Props = {
  emrType: string;
  facilityId: UUID;
  organizationId: UUID;
  mapFaciltiesDict: FacilityMappingStrategyState;
  setMapFaciltiesDict: Dispatch<SetStateAction<FacilityMappingStrategyState>>;
  setDisabledNextBtn: Dispatch<SetStateAction<boolean>>;
  isOnboardFacility: boolean;
  facilitiesFileInputRef: React.RefObject<HTMLInputElement>;
} & Pick<
  PropsFromRedux,
  | 'operatorTenantsList'
  | 'siteGroupsList'
  | 'syncFacility'
  | 'isLoadingSyncFacility'
  | 'onboardPatients'
  | 'isLoadingOnboardPatients'
  | 'onboardFacility'
  | 'isLoadingOnboardFacility'
>;

const ActionButtons = ({
  emrType,
  facilityId,
  organizationId,
  mapFaciltiesDict,
  setMapFaciltiesDict,
  setDisabledNextBtn,
  isOnboardFacility,
  facilitiesFileInputRef,
  operatorTenantsList,
  siteGroupsList,
  syncFacility,
  isLoadingSyncFacility,
  onboardPatients,
  isLoadingOnboardPatients,
  onboardFacility,
  isLoadingOnboardFacility,
}: Props) => {
  const [availableTenantsForLinking, setAvailableTenants] = useState<
    OperatorTenantType[] | []
  >([]);
  const patientsFileInputRef = useRef<HTMLInputElement>(null);
  const isBatch = emrType === 'EPIC' || emrType === 'MEDITECH';

  useEffect(() => {
    setAvailableTenants(
      operatorTenantsList?.filter(
        ({ id }) =>
          !Object.values(mapFaciltiesDict).some(
            ({ tenantId, strategy }) =>
              tenantId === id &&
              strategy &&
              [DATA_STRATEGY.LINKED, DATA_STRATEGY.LINK].includes(strategy),
          ),
      ),
    );
  }, [mapFaciltiesDict]);

  const handleOnboardFacility = (facilityId: string) => {
    const facilitiesOnboardFile = facilitiesFileInputRef.current?.files?.[0];
    if (!facilitiesOnboardFile || facilitiesOnboardFile === null) {
      return;
    }
    onboardFacility({
      emrType,
      organizationId,
      facilityId,
      file: facilitiesOnboardFile,
    });
  };

  if (mapFaciltiesDict[facilityId]?.strategy === DATA_STRATEGY.LINK) {
    return (
      <ActionsButtonsWrap>
        <ActionMessage>{intl.formatMessage(messages.linkTenant)}</ActionMessage>
        <SelectBoxForm
          style={{ width: '220px' }}
          options={extractSelectOptions(
            availableTenantsForLinking,
            'name',
            'id',
          )}
          onChange={tenantId => {
            setDisabledNextBtn(true);
            setMapFaciltiesDict(prevState => ({
              ...prevState,
              [facilityId]: {
                ...prevState[facilityId],
                tenantId: tenantId as UUID,
              },
            }));
          }}
          placeholder={intl.formatMessage(messages.selectSites)}
        />
        <Button
          ghost
          className="delete-strategy-button"
          onClick={() => {
            setDisabledNextBtn(true);
            setMapFaciltiesDict(prevState => ({
              ...prevState,
              [facilityId]: {
                ...prevState[facilityId],
                tenantId: null,
                manualGroupIds: null,
                strategy: DATA_STRATEGY.SKIP,
              },
            }));
          }}
        >
          <CloseOutlined />
        </Button>
      </ActionsButtonsWrap>
    );
  } else if (mapFaciltiesDict[facilityId]?.strategy === DATA_STRATEGY.CREATE) {
    return (
      <ActionsButtonsWrap>
        <ActionMessage>{intl.formatMessage(messages.create)}</ActionMessage>
        <SelectBoxForm
          style={{ width: '220px' }}
          mode="multiple"
          options={extractSelectOptions(siteGroupsList, 'name', 'id')}
          onChange={selectedIds => {
            setDisabledNextBtn(true);
            setMapFaciltiesDict(prevState => ({
              ...prevState,
              [facilityId]: {
                ...prevState[facilityId],
                manualGroupIds: Array.isArray(selectedIds)
                  ? (selectedIds as UUID[])
                  : [],
              },
            }));
          }}
          placeholder={intl.formatMessage(messages.selectSiteGroups)}
        />
        <Button
          ghost
          className="delete-strategy-button"
          onClick={() => {
            setDisabledNextBtn(true);
            setMapFaciltiesDict(prevState => ({
              ...prevState,
              [facilityId]: {
                ...prevState[facilityId],
                tenantId: null,
                manualGroupIds: null,
                strategy: DATA_STRATEGY.SKIP,
              },
            }));
          }}
        >
          <CloseOutlined />
        </Button>
      </ActionsButtonsWrap>
    );
  } else if (mapFaciltiesDict[facilityId]?.strategy === DATA_STRATEGY.LINKED) {
    return (
      <ActionsButtonsWrap>
        <FacilityLabel isLinked={true}>
          {mapFaciltiesDict[facilityId]?.tenantName}
        </FacilityLabel>
        <Popconfirm
          title={intl.formatMessage(messages.confirmUnlink)}
          onConfirm={() => {
            setDisabledNextBtn(true);
            setMapFaciltiesDict(prevState => ({
              ...prevState,
              [facilityId]: {
                ...prevState[facilityId],
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                unlinkedTenantRename: `${prevState[facilityId]?.tenantName}-OLD`,
                strategy: DATA_STRATEGY.UNLINK,
              },
            }));
          }}
          okText={intl.formatMessage(messages.yes)}
          cancelText={intl.formatMessage(messages.no)}
          placement="right"
        >
          <Button className="strategy-button">
            {intl.formatMessage(messages.unlink)}
          </Button>
        </Popconfirm>
        {!isBatch && (
          <ActionButton
            isLoading={isLoadingSyncFacility}
            onClick={() => {
              syncFacility({ organizationId, facilityId });
            }}
          >
            {intl.formatMessage(messages.syncFacility)}
          </ActionButton>
        )}
        {isBatch &&
          mapFaciltiesDict[facilityId]?.strategy === DATA_STRATEGY.LINKED && (
            <>
              {isOnboardFacility && (
                <ActionButton
                  isLoading={isLoadingOnboardFacility}
                  onClick={() => {
                    handleOnboardFacility(facilityId);
                  }}
                >
                  {intl.formatMessage(messages.syncFacilityFromFile)}
                </ActionButton>
              )}
              <ActionButton
                isLoading={isLoadingOnboardPatients}
                className="strategy-button"
                onClick={() => patientsFileInputRef.current?.click()}
              >
                {intl.formatMessage(messages.syncPatients)}
              </ActionButton>
              <input
                type="file"
                ref={patientsFileInputRef}
                style={{ display: 'none' }}
                accept=".csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={e => {
                  const file = e.target.files?.[0];
                  file &&
                    onboardPatients({
                      emrType,
                      organizationId,
                      facilityId,
                      file,
                    });
                }}
              />
            </>
          )}
      </ActionsButtonsWrap>
    );
  }

  return (
    <ActionsButtonsWrap>
      <ActionButton
        className="strategy-button"
        onClick={() => {
          setDisabledNextBtn(true);
          setMapFaciltiesDict(prevState => ({
            ...prevState,
            [facilityId]: {
              ...prevState[facilityId],
              strategy: DATA_STRATEGY.LINK,
            },
          }));
        }}
      >
        <FormattedMessage {...messages.linkTenant} /> <LinkOutlined />
      </ActionButton>
      <ActionButton
        className="strategy-button"
        onClick={() => {
          setDisabledNextBtn(true);
          setMapFaciltiesDict(prevState => {
            return {
              ...prevState,
              [facilityId]: {
                ...prevState[facilityId],
                strategy: DATA_STRATEGY.CREATE,
              },
            };
          });
        }}
      >
        <FormattedMessage {...messages.create} /> <ImportOutlined />
      </ActionButton>
      {mapFaciltiesDict[facilityId]?.unlinkedTenantRename && (
        <Popover
          content={
            <Input
              placeholder={intl.formatMessage(messages.renameUnlinkedTenant)}
              defaultValue={
                mapFaciltiesDict[facilityId]?.unlinkedTenantRename || ''
              }
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setDisabledNextBtn(true);
                setMapFaciltiesDict(prevState => {
                  return {
                    ...prevState,
                    [facilityId]: {
                      ...prevState[facilityId],
                      unlinkedTenantRename: e.target.value,
                    },
                  };
                });
              }}
            />
          }
          title={intl.formatMessage(messages.renameUnlinkedTenant)}
          trigger="click"
        >
          <Tooltip title={intl.formatMessage(messages.renameUnlinkedTenant)}>
            <Button className="strategy-button">
              <EditOutlined />
            </Button>
          </Tooltip>
        </Popover>
      )}
      {mapFaciltiesDict[facilityId]?.strategy === DATA_STRATEGY.UNLINK && (
        <Tooltip
          placement="top"
          title={intl.formatMessage(messages.undoUnlink)}
        >
          <Button
            className="strategy-button"
            onClick={() => {
              setDisabledNextBtn(true);
              setMapFaciltiesDict(prevState => ({
                ...prevState,
                [facilityId]: {
                  ...prevState[facilityId],
                  manualGroupIds: null,
                  tenantId: mapFaciltiesDict[facilityId]?.tenantId,
                  strategy: DATA_STRATEGY.LINKED,
                },
              }));
            }}
          >
            <RollbackOutlined />
          </Button>
        </Tooltip>
      )}
    </ActionsButtonsWrap>
  );
};

const messages = defineMessages({
  linkTenant: {
    id: 'components.ActionButtons.linkTenant',
    defaultMessage: 'Link with ',
  },
  create: {
    id: 'components.ActionButtons.create',
    defaultMessage: 'Create under',
  },
  unlink: {
    id: 'components.ActionButtons.unlink',
    defaultMessage: 'Unlink',
  },
  syncFacility: {
    id: 'components.ActionButtons.syncFacility',
    defaultMessage: 'Sync',
  },
  selectSites: {
    id: 'components.ActionButtons.selectSites',
    defaultMessage: 'Select sites',
  },
  selectSiteGroups: {
    id: 'components.ActionButtons.selectSiteGroups',
    defaultMessage: 'Choose site groups',
  },
  confirmUnlink: {
    id: 'components.ActionButtons.confirmUnlink',
    defaultMessage: 'You are about to unlink a site. Is that confirmed?',
  },
  undoUnlink: {
    id: 'components.ActionButtons.undoUnlink',
    defaultMessage: 'Rollback to previous linked facility',
  },
  renameUnlinkedTenant: {
    id: 'components.ActionButtons.renameUnlinkedTenant',
    defaultMessage: 'Rename unlinked site',
  },
  yes: {
    id: 'components.ActionButtons.yes',
    defaultMessage: 'Yes',
  },
  no: {
    id: 'components.ActionButtons.no',
    defaultMessage: 'No',
  },
  syncPatients: {
    id: 'components.ActionButtons.syncPatients',
    defaultMessage: 'Sync Patients',
  },
  noFileSelected: {
    id: 'components.ActionButtons.noFileSelected',
    defaultMessage: 'No file selected',
  },
  invalidFileType: {
    id: 'components.ActionButtons.invalidFileType',
    defaultMessage: 'Invalid file type. Please upload a CSV or Excel file.',
  },
  uploadSuccess: {
    id: 'components.ActionButtons.uploadSuccess',
    defaultMessage: 'File uploaded successfully',
  },
  uploadFailed: {
    id: 'components.ActionButtons.uploadFailed',
    defaultMessage: 'Failed to upload file',
  },
  syncFacilityFromFile: {
    id: 'components.ActionButtons.onboardFacility',
    defaultMessage: 'Sync Facility',
  },
});

export default React.memo(ActionButtons);
